import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import lodashCompact from 'lodash/fp/compact';
import moment from 'moment';

import {
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
} from 'common/dataTableConstants';

import {
  getListingFullAddressString,
} from 'common/listingHelpers';

import {
  getHouseholdListingsByStatus,
} from 'common/householdHelpers';

import {
  getFirstApplicationCompletedDate,
} from 'common/householdApplication/applicationHelpers';

import {
  householdApplicationStatusesToFollowUp,
  HOUSEHOLD_APPLICATION_STATUS_PAID,
  HOUSEHOLD_APPLICATION_STATUS_PARENT_GROUP_TO_CHILDREN_MAPPING,
} from 'common/householdConstants';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrHouseholdBuyersAgentRow from 'client/components/SrHouseholdBuyersAgentRow';

import postWithState from 'client/actions/postWithState';
import {
  actionDataTableLoadItems,
} from 'client/helpers/dataTables';
import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';

function getProcessingTime({
  household,
}) {
  const completedDates = household.listingApplications.map((listingApplication) => {
    return getFirstApplicationCompletedDate(listingApplication.applications);
  })
    .filter((completedDate) => completedDate)
    .sort((completedDateA, completedDateB) => {
      return moment(completedDateA).unix() - moment(completedDateB).unix();
    });

  if (!completedDates.length) {
    return null;
  }

  return moment().diff(moment(completedDates[0]), 'hours');
}

function getProcessingTimeColor(processing) {
  if (processing < 36) {
    return 'green';
  }
  if (processing < 60) {
    return 'orange300';
  }
  return 'red';
}

class SrHouseholdKanbanBoardCard extends Component {
  static propTypes = {
    household: PropTypes.object.isRequired,
    dispatch: PropTypes.func,
    archived: PropTypes.bool,
    householdApplicationStatus: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }

  static defaultProps = {
    archived: false,
  }

  _onToggleCheckbox = (e, household) => {
    e.preventDefault();
    e.stopPropagation();

    const { dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'households',
        where: {
          id: household.id,
        },
        updates: {
          followedUp: !household.followedUp,
        },
      },
    }).then(() => {
      dispatch(actionDataTableLoadItems, {
        dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
        viewId: `household-id-${household.id}`,
        filter: {
          'households.id': household.id,
        },
        count: 1,
      });
    });
  }

  _reloadDataTable = () => {
    const { dispatch, household } = this.props;

    dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
      viewId: `household-id-${household.id}`,
      filter: {
        'households.id': household.id,
      },
      count: 1,
    });
  }

  _onClick = (e) => {
    preventDefaultPropagation(e);

    const {
      onClick,
      household,
    } = this.props;

    onClick(household);
  }

  render() {
    const {
      household,
      archived,
      householdApplicationStatus,
    } = this.props;

    const { leadRenter } = household;
    const {
      householdListingsActive,
      householdListingsArchived,
      allHouseholdListingsAreArchived,
    } = getHouseholdListingsByStatus({ household });
    const applicationStatusChildrenGroup = (
      HOUSEHOLD_APPLICATION_STATUS_PARENT_GROUP_TO_CHILDREN_MAPPING[householdApplicationStatus]
    );

    const householdListings = allHouseholdListingsAreArchived && householdListingsArchived.length
      ? [
        householdListingsArchived.find(({ applications }) =>
          applications.some((application) => applicationStatusChildrenGroup.includes(application.status))
        ),
      ]
      : householdListingsActive;

    const userIdToUserMapping = household.users.reduce((memo, user) => ({
      ...memo,
      [user.id]: user,
    }), {});

    const isPaidColumn = householdApplicationStatus === HOUSEHOLD_APPLICATION_STATUS_PAID;
    const processingTime = isPaidColumn
      ? getProcessingTime({ household })
      : null;

    return (
      <div
        key={household.id}
        className={classnames(
          'householdKanbanBoardCard',
          archived && 'archived',
          allHouseholdListingsAreArchived && !archived && 'allApplicationsArchived'
        )}
        onClick={this._onClick}
      >
        <div className='row first'>
          <div className='headerRight'>
            <div className='userName'>
              {leadRenter.name}
            </div>
          </div>
          {processingTime && (
            <SrText className='text' color={getProcessingTimeColor(processingTime)}>
              {processingTime ? `${processingTime} h` : ''}
            </SrText>
          )}
        </div>
        <SrBox margin='4px 0'>
          <SrText
            font='greycliff'
            fontSize={12}
            weight='500'
            lineHeight='18px'
          >
            App #{household.id}
          </SrText>
        </SrBox>
        <div className='listings'>
          {householdListings.map((householdListing) => (
            <div key={householdListing.listing.id} className='relative'>
              <div className='text oneLine'>
                {getListingFullAddressString(householdListing.listing)}
              </div>
              {householdListing.listing.partnerId && (
                <div className='partnerName'>
                  {lodashCompact([
                    householdListing.listing.partnerDisplayName,
                    householdListing.listing.partnerBrokerName,
                  ]).join(' - ')}
                </div>
              )}
              {householdListing.hasBuyersAgent && (
                <SrHouseholdBuyersAgentRow
                  className='small'
                  buyersAgentName={householdListing.buyersAgentName}
                  buyersAgentPhone={householdListing.buyersAgentPhone}
                  buyersAgentEmail={householdListing.buyersAgentEmail}
                />
              )}
              <div className='kanbanBoardCardApplicationStatusesContainer'>
                {householdListing.applications.map((application, index) => (
                  <span className='kanbanBoardCardApplicationStatusRow' key={application.id}>
                    <span>{`${userIdToUserMapping[application.userId].name}: `}</span>
                    <span
                      className={classnames({
                        active: !applicationStatusChildrenGroup.includes(application.status),
                      })}
                    >
                      {application.status}
                    </span>
                    {index !== householdListing.applications.length - 1 && (
                      <span> |</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {householdApplicationStatusesToFollowUp.includes(householdApplicationStatus) && (
            <div
              className={classnames('checkbox', {
                active: household.followedUp,
                hidden: archived,
              })}
              onClick={(e) => this._onToggleCheckbox(e, household)}
            />
          )}
        </div>
      </div>
    );
  }
}

SrHouseholdKanbanBoardCard.displayName = 'SrHouseholdKanbanBoardCard';

export default SrHouseholdKanbanBoardCard;
